import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const ScannerAttendance = () => {
  const [scanInput, setScanInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const inputRef = useRef(null);

  // Keep focus on the hidden/transparent input field so scanner always types into it
  useEffect(() => {
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    
    // Focus immediately
    focusInput();

    // Re-focus if user clicks anywhere else
    document.addEventListener('click', focusInput);
    
    return () => {
      document.removeEventListener('click', focusInput);
    };
  }, []);

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;

    setLoading(true);
    const dataToSend = scanInput.trim();
    setScanInput(''); // Clear input for next scan immediately

    try {
      const response = await axios.post('/api/attendance/scan', {
        scan_data: dataToSend
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 1500,
          showConfirmButton: false
        });
        
        // Add to recent scans list
        setRecentScans(prev => [
          {
            id: Date.now(),
            user: response.data.user,
            type: response.data.type,
            time: new Date().toLocaleTimeString(),
            status: 'success'
          },
          ...prev
        ].slice(0, 10)); // Keep only last 10
      }
    } catch (error) {
      console.error('Scan error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to record attendance.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        timer: 1500,
        showConfirmButton: false
      });
      
      setRecentScans(prev => [
        {
          id: Date.now(),
          scanData: dataToSend,
          error: errorMsg,
          time: new Date().toLocaleTimeString(),
          status: 'error'
        },
        ...prev
      ].slice(0, 10));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark mb-0">Quick Scan Attendance</h2>
      </div>

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-5 text-center d-flex flex-column justify-content-center align-items-center">
              
              <div className="mb-4">
                <i className="bi bi-upc-scan text-primary" style={{ fontSize: '5rem' }}></i>
              </div>
              
              <h4 className="fw-bold mb-3">Ready to Scan</h4>
              <p className="text-muted mb-4">
                Please connect your USB Barcode / QR Code Scanner. Focus is automatically locked, just scan the ID Card!
              </p>

              <form onSubmit={handleScanSubmit} className="w-100 position-relative">
                {/* Visual Fake Input for UX */}
                <div className={`p-3 rounded-3 border ${loading ? 'border-warning' : 'border-primary'}`} style={{ backgroundColor: '#f8fafc', transition: 'all 0.3s' }}>
                  <div className="d-flex align-items-center justify-content-center">
                    {loading ? (
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    ) : (
                      <div className="pulse-dot bg-success rounded-circle me-2" style={{ width: '10px', height: '10px' }}></div>
                    )}
                    <span className="fw-bold text-secondary">
                      {loading ? 'Processing...' : 'Listening for scanner...'}
                    </span>
                  </div>
                </div>

                {/* The actual input hidden from view but active for scanner keystrokes */}
                <input
                  ref={inputRef}
                  type="text"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  autoFocus
                  style={{ 
                    position: 'absolute', 
                    top: 0, left: 0, 
                    width: '100%', height: '100%', 
                    opacity: 0, 
                    cursor: 'default' 
                  }}
                  autoComplete="off"
                />
              </form>

            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
              <h5 className="fw-bold mb-0"><i className="bi bi-clock-history me-2 text-primary"></i>Recent Scans</h5>
            </div>
            <div className="card-body p-4">
              {recentScans.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                  <p>No recent scans yet.</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {recentScans.map((scan) => (
                    <div key={scan.id} className="list-group-item px-0 py-3 d-flex align-items-center border-bottom border-light">
                      <div className={`rounded-circle p-2 me-3 ${scan.status === 'success' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                        <i className={`bi ${scan.status === 'success' ? 'bi-check-lg' : 'bi-x-lg'} fs-5`}></i>
                      </div>
                      <div className="flex-grow-1">
                        {scan.status === 'success' ? (
                          <>
                            <h6 className="mb-0 fw-bold">
                              {scan.user.first_name} {scan.user.last_name}
                              <span className="badge bg-light text-dark border ms-2">
                                {scan.type === 'student' ? 'Student' : 'Staff'}
                              </span>
                            </h6>
                            <small className="text-muted">
                              {scan.type === 'student' ? (scan.user.reg_no || `ID: ${scan.user.id}`) : (scan.user.designation || 'Staff')}
                            </small>
                          </>
                        ) : (
                          <>
                            <h6 className="mb-0 fw-bold text-danger">Scan Failed</h6>
                            <small className="text-muted">{scan.error}</small>
                          </>
                        )}
                      </div>
                      <div className="text-end">
                        <span className="badge bg-light text-secondary rounded-pill px-3 py-2 border">
                          <i className="bi bi-clock me-1"></i> {scan.time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScannerAttendance;
